/*
  Отчёты — §3.11.3, §3.11.4.

  Экран, ради которого клиент решает, продлевать ли договор. Порядок разделов
  отсюда и следует: **первыми открываются счётчики окупаемости** - деньги за всё
  время работы, - а месячный отчёт и метрики портфеля лежат вкладками ниже
  (решение владельца 08.09.2026). До этого экран шёл одной простынёй, и главная
  цифра оказывалась внизу, куда доезжали не все.

  🔴 Заголовок экрана - «Отчёты», а не «Что принёс Арбитр» (указание владельца
  29.09.2026). Прежний называл не экран, а его первый блок, и после того, как
  наверху появился переключатель видов, стал прямо неверным: на вкладках
  «Графики» и «Полоски» под заголовком «Что принёс Арбитр» не было ни одного
  счётчика окупаемости. Название блока осталось там, где оно и означает блок, -
  на дашборде («Что принёс Арбитр с такого-то числа»).

  Правило, которое здесь важнее вёрстки: **неизвестная величина показывается как
  неизвестная**, а не как ноль. Сервер отдаёт `null` вместе с объяснением, и
  экран обязан эту разницу сохранить - ноль это измеренное отсутствие, `null` -
  отсутствие измерения.

  Стоимость платформы стоит в том же блоке, что и эффект: мы сами предъявляем
  клиенту счёт к сравнению, и это сильнее любой презентации при продлении.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { api } from '../api/client.js'
import { Field, Loading, Notice, Select } from '../components/Common.jsx'
import MonthlyReport from '../components/MonthlyReport.jsx'
import PaybackTiles from '../components/PaybackTiles.jsx'
import PeriodPicker from '../components/PeriodPicker.jsx'
import PortfolioBars from '../components/PortfolioBars.jsx'
import PortfolioCharts from '../components/PortfolioCharts.jsx'
import PortfolioMetrics from '../components/PortfolioMetrics.jsx'
import PortfolioSparks from '../components/PortfolioSparks.jsx'
import Reconciliation from '../components/Reconciliation.jsx'
import Tabs, { TabPanel } from '../components/Tabs.jsx'
import { lastMonths, monthKey, periodStart, previousMonth, today } from '../api/format.js'

const FORMATS = {
  pdf: 'PDF - прочитать и подшить',
  xlsx: 'Excel - считать в таблице',
  csv: 'CSV - для бухгалтерии',
}

const MONTHS_TO_CHOOSE = 12

const TABS = [
  { key: 'monthly', title: 'Отчёт за месяц' },
  { key: 'reconciliation', title: 'Сверка поступлений' },
  { key: 'metrics', title: 'Метрики портфеля' },
]

/*
  Верхний переключатель (29.09.2026, указание владельца: «табами отдельными на
  уровне плашек - либо плашки, либо графики, либо полоски»). Показывается ровно
  один вид: три блока подряд дали бы экран на пять тысяч пикселей, а главная
  цифра - чистый эффект - уехала бы за сгиб, ровно туда, откуда её однажды уже
  поднимали.

  Вид у него свой (`nested` - переключатель на подложке): двумя одинаковыми
  рядами вкладок подряд человек читает один сбившийся ряд.
*/
const VIEWS = [
  { key: 'tiles', title: 'Плашки' },
  { key: 'charts', title: 'Графики' },
  { key: 'bars', title: 'Полоски' },
]

export default function ReportsPage() {
  const [report, setReport] = useState(null)
  const [analytics, setAnalytics] = useState(null)
  const [monthly, setMonthly] = useState(null)
  const [reconciliation, setReconciliation] = useState(null)
  const [detail, setDetail] = useState(null)
  /*
    Разбор портфеля для полосы «где лежат деньги» берётся у дашборда и грузится
    ТОЛЬКО при открытии вкладки «Полоски»: это самый тяжёлый ответ сервера, и
    платить за него на каждом открытии отчётов незачем - полосу смотрят не
    каждый раз.
  */
  const [dashboard, setDashboard] = useState(null)
  const [month, setMonth] = useState(() => previousMonth())
  /*
    Период хранится ДАТАМИ, а не названием: это ровно то, что уходит на сервер и
    что видно человеку в поле выбора. Название («последний квартал») через год
    означало бы другой отрезок, и повторить прошлую цифру было бы нечем.
  */
  const [range, setRange] = useState(() => ({
    since: periodStart('year'),
    until: today(),
  }))
  /*
    Начало периода «за всё время» держится в ссылке, а не в состоянии, и это не
    оптимизация. Оно приходит в ответе, от которого само же и зависит: положи его
    в состояние - и загрузка будет пересоздавать себя после каждого ответа,
    делая лишний запрос на ровном месте.

    27.10.2026, замечание владельца: раньше здесь был день первой загрузки, и
    «за всё время» оказывалось уже, чем «с начала года», - цифры уменьшались.
    Теперь это самая ранняя запись, которую считает отчёт (`history_from`).
  */
  const historyFrom = useRef(null)
  const [format, setFormat] = useState('pdf')
  const [tab, setTab] = useState('monthly')
  const [view, setView] = useState('tiles')
  const [error, setError] = useState(null)
  const [loadedMonth, setLoadedMonth] = useState(null)
  const [exportError, setExportError] = useState(null)
  const [saving, setSaving] = useState(false)

  const months = useMemo(() => lastMonths(MONTHS_TO_CHOOSE), [])
  const isRunning = month === monthKey(new Date())

  /*
    Ответ на устаревший запрос выбрасывается (аудит 26.10.2026, причина 5): при быстрой
    смене месяца или периода ответы приходят не в порядке запросов, и под новым периодом
    оседали цифры прошлого. Эффект ниже снимает отметку при уходе и перед следующим
    запросом, как в реестре (09.09.2026).
  */
  const load = useCallback(() => {
    setError(null)
    let current = true
    Promise.all([
      api.payback(range.since, range.until),
      api.analytics(),
      api.monthlyReport(month),
      api.reconciliation(month),
      api.portfolio(range.since, range.until),
    ])
      .then(([gotPayback, gotAnalytics, gotMonthly, gotReconciliation, gotDetail]) => {
        if (!current) return
        setReport(gotPayback)
        setAnalytics(gotAnalytics)
        setMonthly(gotMonthly)
        setReconciliation(gotReconciliation)
        setDetail(gotDetail)
        setLoadedMonth(month)
        historyFrom.current = gotPayback.history_from
      })
      .catch((failure) => {
        if (current) setError(failure)
      })
    return () => {
      current = false
    }
  }, [month, range])

  /*
    Полоса портфеля догружается при первом открытии вкладки «Полоски» и больше
    не перезапрашивается: она показывает срез на дату расчёта, и от смены
    периода не меняется. Ошибка сюда же, в общий `error` экрана: отдельного
    места под неё на вкладке нет, а молча пустая полоса читалась бы как «в
    портфеле ничего нет».
  */
  useEffect(() => {
    if (view !== 'bars' || dashboard) return
    api.dashboard().then(setDashboard).catch(setError)
  }, [view, dashboard])

  const changeRange = useCallback((since, until) => setRange({ since, until }), [])

  useEffect(() => load(), [load])

  const save = useCallback(async () => {
    setExportError(null)
    setSaving(true)
    try {
      await api.downloadMonthlyReport(month, format)
    } catch (failure) {
      setExportError(failure)
    } finally {
      setSaving(false)
    }
  }, [month, format])

  // Сбой не стирает отчёт, который уже на экране (причина 1): без данных - одна
  // плашка, с данными - плашка над ними.
  if (!report || !analytics || !monthly || !detail) {
    return error ? <Notice error={error} /> : <Loading what="Считаем" />
  }
  // Цифры месячных вкладок относятся к месяцу, для которого их получили: пока
  // идёт запрос нового месяца, прежние под новым названием не показываются.
  const monthReady = loadedMonth === month
  // Запрос месяца упал - не «считаем» навсегда, а отсылка к причине над вкладками
  // (ревью 30.10.2026).
  const monthPending = error ? (
    <p className="muted">Месяц не посчитан: причина - в сообщении выше.</p>
  ) : (
    <Loading what="Считаем месяц" />
  )

  return (
    <>
      {/*
        Шапка прикреплена к верху рабочей зоны (08.09.2026, решение владельца):
        экран длинный, а период - то, чем человек управляет по ходу чтения.
        Уехавший наверх выбор означал бы прокрутку туда и обратно ради каждой
        смены периода.

        Сам период написан ровно в одном месте - в поле выбора. Подпись «с ... по
        ...» под заголовком убрана: одна и та же вещь в двух местах рано или
        поздно разъезжается, и человек верит той, что заметнее.
      */}
      <header className="head head--sticky">
        <h1 className="head__title">Отчёты</h1>
        <PeriodPicker
          since={report.since}
          until={report.until}
          historyFrom={historyFrom.current}
          onChange={changeRange}
        />
      </header>

      <Notice error={error} wide />

      <Tabs tabs={VIEWS} active={view} onChange={setView} nested />

      {view === 'tiles' ? (
        <>
          <PaybackTiles report={report} />
          <PortfolioSparks detail={detail} />
        </>
      ) : null}
      {view === 'charts' ? <PortfolioCharts detail={detail} /> : null}
      {view === 'bars' ? <PortfolioBars dashboard={dashboard} detail={detail} /> : null}

      <Tabs tabs={TABS} active={tab} onChange={setTab} />

      <TabPanel tabKey="monthly" active={tab}>
        <div className="row">
          <Field label="Отчётный месяц" width="s">
            <Select value={month} onChange={setMonth} options={months} empty={null} />
          </Field>
          <Field label="Формат выгрузки" width="m">
            <Select value={format} onChange={setFormat} options={FORMATS} empty={null} />
          </Field>
          <button type="button" className="button" onClick={save} disabled={saving}>
            {saving ? 'Готовим файл…' : 'Выгрузить'}
          </button>
        </div>
        {/* Пояснение к строке с кнопкой стоит под ней, а не под своим полем. */}
        {monthReady ? (
          <p className="muted report__note">
            {monthly.title}.{' '}
            {isRunning
              ? 'Месяц ещё идёт: показано то, что сделано на сегодня.'
              : 'Месяц закрыт: цифры больше не изменятся.'}
          </p>
        ) : null}
        <Notice error={exportError} />

        {monthReady ? <MonthlyReport report={monthly} /> : monthPending}
      </TabPanel>

      <TabPanel tabKey="reconciliation" active={tab}>
        <div className="row">
          <Field label="Месяц сверки" width="s">
            <Select value={month} onChange={setMonth} options={months} empty={null} />
          </Field>
        </div>
        {monthReady ? <Reconciliation summary={reconciliation} month={month} /> : monthPending}
      </TabPanel>

      <TabPanel tabKey="metrics" active={tab}>
        <PortfolioMetrics analytics={analytics} />
      </TabPanel>
    </>
  )
}
